import { enqueueSnackbar } from 'notistack';

import {
  Box,
  Card,
  Grid,
  Link,
  Stack,
  Divider,
  Skeleton,
  CardHeader,
  IconButton,
  Typography,
  CardContent
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useCustomFields } from 'src/hooks/use-custom-fields';

import { fDate } from 'src/utils/format-time';
import { fCurrency } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';
import { getCreateJobErrorMessage } from 'src/services/deal-flow/deal-flow.utils';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { useGetDealQuery, useCreateJobForDealMutation } from 'src/services/deal-flow/deal-flow.service';

import Iconify from 'src/components/iconify';
import { DetailRow } from 'src/components/detail-row/detail-row';
import DealStatusLabel from 'src/components/deal-status/deal-status-label';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

// ----------------------------------------------------------------------

type Props = {
  dealId: string;
};

type DetailRowProps = {
  label: string;
  value: string | number | null | undefined;
  isHighlight?: boolean;
  link?: string | undefined;
};

export function DealDetailSection({ dealId }: Props) {
  const { t } = useTranslate();

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.DEAL,
    entityId: dealId
  });

  const [createJobForDeal, { isLoading: isCreating }] = useCreateJobForDealMutation();

  const { data: deal, isLoading } = useGetDealQuery({ dealId }, {
    skip: !dealId,
  });

  const handleCreateJob = async () => {
    if (isCreating || !deal) return;

    try {
      await createJobForDeal({ dealId: deal.id })
        .unwrap();

      enqueueSnackbar(t('deal_flow.api.create_job.success'), {
        variant: 'success',
      });

    }
    catch (error) {
      enqueueSnackbar(getCreateJobErrorMessage(error), {
        variant: 'error',
      });

    }
  }

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!deal) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('deal_flow.detail.no_deal_found')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('deal_flow.detail.no_deal_found_message')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Deal Overview Card */}
      <Card elevation={2}>
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('deal_flow.detail.deal_overview')}
            </Typography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={0}>
                <DetailRow
                  label={t('deal_flow.detail.value')}
                  value={deal.value ? fCurrency(deal.value, deal.pipeline.currency?.code ?? 'USD') : undefined}
                  isHighlight={!!deal.value}
                />
                <Divider sx={{ my: 0.5 }} />
                <DetailRow
                  label={t('deal_flow.detail.type')}
                  value={deal.type ? t(`enums.deal_type.${deal.type}`) : undefined}
                />
                <Divider sx={{ my: 0.5 }} />
                <DetailRow
                  label={t('deal_flow.detail.close_date')}
                  value={deal.close_date ? fDate(deal.close_date) : undefined}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={0}>
                <DetailRow
                  label={t('deal_flow.detail.pipeline')}
                  value={deal.pipeline?.name}
                  link={paths.dashboard.deal_flow.pipeline(deal.pipeline?.id || '')}
                />
                <Divider sx={{ my: 0.5 }} />
                <DetailRow
                  label={t('deal_flow.detail.stage')}
                  value={deal.stage?.name}
                />
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ py: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        minWidth: 120,
                        flexShrink: 0
                      }}
                    >
                      {t('common.status')}
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <DealStatusLabel status={deal.status} />
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Job Information Card */}
      <Card elevation={2}>
        <CardHeader
          title={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('deal_flow.detail.job_information')}
              </Typography>

              {
                deal.job?.id ? (
                  <Link
                    href={paths.dashboard.jobs.detail(deal.job.id)}
                    style={{ cursor: 'pointer' }}
                    color="inherit"
                    component={RouterLink}
                    underline="hover"
                    target="_blank"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}
                      >
                        {t('deal_flow.detail.view_job')}
                      </Typography>
                      <IconButton size="small" color="primary">
                        <Iconify icon="lucide:external-link" />
                      </IconButton>
                    </Stack>
                  </Link>
                ) : (
                  <Link
                    onClick={handleCreateJob}
                    style={{ cursor: 'pointer' }}
                    underline="hover"
                  >

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}
                      >
                        {t('deal_flow.detail.create_job')}
                      </Typography>
                      <IconButton size="small" color="primary">
                        <Iconify icon="lucide:plus" />
                      </IconButton>
                    </Stack>

                  </Link>
                )}

            </Stack>

          }
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={0}>
                <DetailRow
                  label={t('deal_flow.detail.job_title')}
                  value={deal.job_title}
                  isHighlight={!!deal.job_title}
                />
                <Divider sx={{ my: 0.5 }} />
                <DetailRow
                  label={t('deal_flow.detail.job_vacancies')}
                  value={deal.job_vacancies}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={0}>
                <DetailRow
                  label={t('deal_flow.detail.job_recruitment_type')}
                  value={deal.job_recruitment_type ? t(`enums.recruitment_type.${deal.job_recruitment_type}`) : undefined}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Description Card */}
      {
        deal.description && (
          <Card elevation={2}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('deal_flow.detail.description')}
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <TruncatedTextField
                text={deal.description}
                mode='lines'
                limit={5}
                sx={{
                  lineHeight: 1.6,
                  color: 'text.primary',
                  whiteSpace: 'pre-wrap'
                }}
                variant="body2"
                showMoreEnabled

              />
            </CardContent>
          </Card>
        )
      }

      {/* Custom fields Card */}
      {
        customFields.hasCustomFields && (
          <Card elevation={1}>
            <CardHeader
              title={
                <Typography variant="subtitle1">
                  {t('common.custom_fields')}
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {customFields.isLoading ? (
                    <Skeleton variant="rectangular" width="100%" height={48} />
                  ) : (
                    customFields.getFields().map((field, index) => (
                      <>
                        <DetailRow
                          key={index}
                          label={field.label}
                          value={field.value}
                          isHighlight={!!field.value}
                        />
                        {index < customFields.getFields().length - 1 && (
                          <Divider sx={{ my: 0.5 }} />
                        )}
                      </>
                    ))
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )
      }

      {/* metadata Card */}
      <Card elevation={1} sx={{ bgcolor: 'grey.50' }}>
        <CardHeader
          title={
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t('deal_flow.detail.metadata')}
            </Typography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DetailRow
                label={t('deal_flow.detail.created_by')}
                value={deal.created_by ? `${deal.created_by.first_name} ${deal.created_by.last_name}` : undefined}
                isHighlight
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailRow
                label={t('deal_flow.detail.created_at')}
                value={deal.created_at ? fDate(deal.created_at) : undefined}
                isHighlight
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

    </Stack >
  );
}

function LoadingSkeleton() {
  return (
    <Stack spacing={3}>
      {[1, 2, 3].map((index) => (
        <Card key={index}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Skeleton variant="text" width="40%" height={24} />
              {[1, 2, 3, 4].map((item) => (
                <Box key={item} sx={{ py: 1 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Skeleton variant="text" width="30%" />
                    <Skeleton variant="text" width="40%" />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}